import { useToast } from "@eudonet/opal";
import { useI18n } from "vue-i18n";
/**
 * Shared auto-close duration for success toasts.
 *
 * DS Toast spec: success auto-closes after 5s (<=8 words) / 6s (9-15 words) / 7s (>15 words).
 * The T-S-19 rewording put most success messages in the 9-15 word band, so 6s is the fit.
 * Import this wherever a success toast is shown through a raw `showToast` call instead of
 * re-hardcoding the number, so the duration stays uniform across screens.
 */
export const SUCCESS_TOAST_DURATION_MS = 6000;
/**
 * App-wide toast helper. Wraps OPAL's `useToast()` with the project's standard
 * success/error defaults (duration, aria-close label) so call sites only need
 * to pass an i18n key.
 *
 * Consolidates what used to be per-module copies (`useOrganisationToast`,
 * `useFinanceToast`, `useWorkspaceToast`) — they were byte-identical and added
 * no module-specific behavior.
 */
export function useAppToast() {
    const { showToast } = useToast();
    const { t } = useI18n();
    /**
     * `plural` drives vue-i18n's pipe-plural choice for count-based messages
     * ("1 consentement a été mis à jour… | N consentements ont été mis à jour…").
     */
    function toastSuccess(messageKey, params, plural) {
        showToast({
            message: plural === undefined
                ? params
                    ? t(messageKey, params)
                    : t(messageKey)
                : t(messageKey, params ?? {}, plural),
            ariaClose: t("form.dismissToast"),
            status: "success",
            duration: SUCCESS_TOAST_DURATION_MS,
        });
    }
    /** No duration: OPAL keeps error toasts until the user closes them. */
    function toastError(messageKey, params) {
        showToast({
            message: params ? t(messageKey, params) : t(messageKey),
            ariaClose: t("form.dismissToast"),
            status: "error",
        });
    }
    function toastInfo(messageKey, params) {
        showToast({
            message: params ? t(messageKey, params) : t(messageKey),
            ariaClose: t("form.dismissToast"),
            status: "info",
            duration: 4000,
        });
    }
    return { toastSuccess, toastError, toastInfo };
}
